import { useState } from 'react';
import {
  ActionIcon,
  Button,
  Center,
  Container,
  Group,
  Loader,
  Paper,
  Stack,
  Table,
  Text,
  ThemeIcon,
  Title,
  Tooltip,
} from '@mantine/core';
import { IconBuildingSkyscraper, IconChevronRight, IconPlus, IconTrash } from '@tabler/icons-react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useSubscription } from 'urql';
import { ErrorAlert } from '@/components/error-alert';
import { getErrorMessage } from '@/lib/error-message';
import { CreateOrganizationModal } from './-components/create-organization-modal';
import { DeleteOrganizationModal } from './-components/delete-organization-modal';
import { SystemOrganizationsSubscription } from './-components/graphql';

export const Route = createFileRoute('/_authed/system/organizations/')({
  component: SystemOrganizationsPage,
});

type OrganizationTarget = { id: string; name: string };

function EmptyOrganizations({ onCreate }: { onCreate: () => void }) {
  return (
    <Paper p="xl" withBorder radius="md">
      <Stack align="center" gap="md">
        <ThemeIcon size={48} radius="xl" variant="light" color="gray">
          <IconBuildingSkyscraper size={24} />
        </ThemeIcon>
        <Stack gap={2} align="center">
          <Text fw={500}>No organizations yet</Text>
          <Text size="sm" c="dimmed">
            Create your first organization to get started.
          </Text>
        </Stack>
        <Button variant="light" leftSection={<IconPlus size={16} />} onClick={onCreate}>
          Create Organization
        </Button>
      </Stack>
    </Paper>
  );
}

function SystemOrganizationsPage() {
  const navigate = useNavigate();
  const [createOpened, setCreateOpened] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<OrganizationTarget | null>(null);

  const [{ data, error }] = useSubscription({ query: SystemOrganizationsSubscription });

  if (error) {
    return (
      <Container size="xl" py="xl">
        <ErrorAlert message={getErrorMessage(error, 'Unable to load organizations')} />
      </Container>
    );
  }

  if (!data) {
    return (
      <Center h="50vh">
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <>
      <Container size="xl" py="xl">
        <Stack gap="lg">
          <Group justify="space-between" align="flex-end">
            <Stack gap={2}>
              <Title order={2}>Organizations</Title>
              {data.organizations.length > 0 && (
                <Text size="sm" c="dimmed">
                  {data.organizations.length} organization{data.organizations.length === 1 ? '' : 's'}
                </Text>
              )}
            </Stack>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setCreateOpened(true)}>
              Create Organization
            </Button>
          </Group>

          {data.organizations.length === 0 ? (
            <EmptyOrganizations onCreate={() => setCreateOpened(true)} />
          ) : (
            <Paper p={0} withBorder radius="md" style={{ overflow: 'hidden' }}>
              <Table highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Name</Table.Th>
                    <Table.Th>Slug</Table.Th>
                    <Table.Th ta="right">Members</Table.Th>
                    <Table.Th ta="right">Numbers</Table.Th>
                    <Table.Th>CRM</Table.Th>
                    <Table.Th>Created</Table.Th>
                    <Table.Th w={80} />
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.organizations.map((organization) => (
                    <Table.Tr
                      key={organization.id}
                      style={{ cursor: 'pointer' }}
                      onClick={() =>
                        void navigate({ to: '/system/organizations/$id', params: { id: organization.id } })
                      }
                    >
                      <Table.Td>
                        <Text fw={600} size="sm">
                          {organization.name}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed" ff="monospace">
                          {organization.slug}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" c="dimmed">
                          {organization.membersAggregate.aggregate?.count ?? 0}
                        </Text>
                      </Table.Td>
                      <Table.Td ta="right">
                        <Text size="sm" c="dimmed">
                          {organization.phoneNumbersAggregate.aggregate?.count ?? 0}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        {organization.crmIntegration ? (
                          <Text size="sm" c="green.6">
                            {organization.crmIntegration.name}
                          </Text>
                        ) : (
                          <Text size="sm" c="dimmed">
                            —
                          </Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {new Date(organization.createdAt).toLocaleDateString()}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs" justify="flex-end" wrap="nowrap">
                          <Tooltip label="Delete organization">
                            <ActionIcon
                              variant="subtle"
                              color="red"
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteTarget({ id: organization.id, name: organization.name });
                              }}
                            >
                              <IconTrash size={16} />
                            </ActionIcon>
                          </Tooltip>
                          <IconChevronRight size={16} style={{ color: 'var(--mantine-color-dimmed)' }} />
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>
          )}
        </Stack>
      </Container>

      {createOpened && <CreateOrganizationModal onClose={() => setCreateOpened(false)} />}

      {deleteTarget && (
        <DeleteOrganizationModal
          organizationId={deleteTarget.id}
          organizationName={deleteTarget.name}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => setDeleteTarget(null)}
        />
      )}
    </>
  );
}
